import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { IntroNote } from "@/components/IntroNote";
import { Layers, ShieldCheck, ArrowUpRight, Zap } from "lucide-react";

const EFFORT_LABEL = { S: "Small", M: "Medium", L: "Large" };

export default function Frameworks() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api
      .get("/frameworks")
      .then(({ data }) => setItems(data.items))
      .catch(() => toast.error("Failed to load frameworks"));
  }, []);

  const openFramework = (id) => {
    setLoadingDetail(true);
    setSelected({ loading: true });
    api
      .get(`/frameworks/${id}`)
      .then(({ data }) => setSelected(data))
      .catch(() => {
        toast.error("Failed to load framework");
        setSelected(null);
      })
      .finally(() => setLoadingDetail(false));
  };

  const ekselens = items.filter((f) => !f.is_erio_seed);
  const seeds = items.filter((f) => f.is_erio_seed);

  const renderCard = (f) => (
    <button
      key={f.id}
      onClick={() => openFramework(f.id)}
      data-testid={`framework-card-${f.id}`}
      className="group relative flex flex-col items-start border border-black/10 bg-white p-6 text-left transition-all hover:-translate-y-0.5 hover:border-ek-purple"
    >
      <span className="absolute left-0 top-0 h-full w-0.5 origin-top scale-y-0 bg-ek-purple transition-transform group-hover:scale-y-100" />
      <div className="flex items-center gap-2">
        {f.is_erio_seed ? (
          <ShieldCheck size={16} className="text-ek-purple" />
        ) : (
          <Layers size={16} className="text-ek-purple" />
        )}
        <span className="text-[0.7rem] font-medium uppercase tracking-wider text-ek-ink/65">
          v{f.version}
        </span>
      </div>
      <h3 className="mt-3 font-display text-lg font-semibold text-ek-ink">{f.name}</h3>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ek-ink/65">
        <span>{f.dimension_count} dimensions</span>
        <span>{f.indicator_count} indicators</span>
        <span>{f.csi_move_count} CSI moves</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(f.public_anchors || []).slice(0, 3).map((a) => (
          <span
            key={a}
            className="bg-ek-canvas px-2 py-0.5 text-[0.65rem] font-medium text-ek-ink/65"
          >
            {a.split("(")[0].split("—")[0].trim()}
          </span>
        ))}
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="frameworks-page">
        <div>
          <p className="ek-eyebrow">EKSELENS360 · Risk Graph anchor</p>
          <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Maturity Frameworks</h1>
          <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
            The calibrated rubrics behind every risk score — anchored to public
            standards (NIST CSF 2.0, ITIL 4, DORA, ISO 27001) rather than invented.
          </p>
        </div>

        <IntroNote icon={Layers} title="What you're looking at" testid="frameworks-intro" className="mt-6">
          The calibrated rubrics that turn judgement into a defensible score — each anchored to a recognised
          public standard rather than invented. Open any framework to see its dimensions, the indicators behind
          each score, and the improvement (CSI) moves that lift maturity.
        </IntroNote>

        <section className="mt-10">
          <h2 className="ek-eyebrow mb-4 text-ek-ink/65">EKSELENS360 frameworks</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ekselens.map(renderCard)}
          </div>
        </section>

        {seeds.length > 0 && (
          <section className="mt-12">
            <h2 className="ek-eyebrow mb-4 text-ek-ink/65">ERIO-native seeds</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {seeds.map(renderCard)}
            </div>
          </section>
        )}
      </main>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-2xl"
          data-testid="framework-detail"
        >
          {selected && selected.loading && (
            <p className="mt-10 text-sm text-ek-ink/65">Loading framework…</p>
          )}
          {selected && !selected.loading && (
            <>
              <SheetHeader>
                <p className="ek-eyebrow">{selected.service_slug}</p>
                <SheetTitle className="font-display text-2xl text-ek-ink">
                  {selected.name}
                </SheetTitle>
                <SheetDescription>{selected.provenance || `Version ${selected.version}`}</SheetDescription>
              </SheetHeader>

              {/* Public anchors */}
              <div className="mt-6">
                <h3 className="ek-eyebrow mb-2">Public anchors</h3>
                <div className="flex flex-wrap gap-1.5" data-testid="framework-anchors">
                  {(selected.public_anchors || []).map((a) => (
                    <span
                      key={a}
                      className="border border-ek-purple/20 bg-ek-purple/5 px-2 py-0.5 text-xs text-ek-ink/70"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>

              {/* Dimensions + indicators */}
              <div className="mt-8">
                <h3 className="ek-eyebrow mb-3">
                  Dimensions ({(selected.dimensions || []).length})
                </h3>
                <Accordion type="single" collapsible className="space-y-2">
                  {(selected.dimensions || []).map((d) => (
                    <AccordionItem
                      key={d.id}
                      value={d.id}
                      className="border border-black/10 bg-white px-4"
                      data-testid={`framework-dimension-${d.id}`}
                    >
                      <AccordionTrigger className="py-3 text-left hover:no-underline">
                        <div>
                          <span className="font-display text-sm font-semibold text-ek-ink">
                            {d.name}
                          </span>
                          <span className="ml-2 text-xs text-ek-ink/65">
                            {(d.indicators || []).length} indicators
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {d.anchor && (
                          <p className="mb-3 text-xs italic text-ek-ink/65">{d.anchor}</p>
                        )}
                        <div className="space-y-3">
                          {(d.indicators || []).map((ind) => (
                            <div key={ind.id} className="border-l-2 border-ek-purple/20 pl-3">
                              <p className="text-sm font-medium text-ek-ink">{ind.prompt}</p>
                              {ind.evidence_hint && (
                                <p className="mt-0.5 text-xs text-ek-ink/65">
                                  Evidence: {ind.evidence_hint}
                                </p>
                              )}
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {(ind.options || []).map((o) => (
                                  <span
                                    key={o.score}
                                    title={o.label}
                                    className="bg-ek-canvas px-1.5 py-0.5 text-[0.65rem] font-semibold text-ek-ink/65"
                                  >
                                    {o.score}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* CSI moves */}
              {(selected.csi_moves || []).length > 0 && (
                <div className="mt-8">
                  <h3 className="ek-eyebrow mb-3 flex items-center gap-1">
                    <Zap size={12} /> CSI moves ({selected.csi_moves.length})
                  </h3>
                  <div className="space-y-2" data-testid="framework-csi-moves">
                    {selected.csi_moves.map((m, i) => (
                      <div
                        key={i}
                        className="flex items-start justify-between gap-3 border border-black/10 bg-white p-3"
                      >
                        <div className="flex items-start gap-2">
                          <ArrowUpRight size={14} className="mt-0.5 text-ek-purple" />
                          <span className="text-sm text-ek-ink/80">{m.name}</span>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1.5">
                          <span
                            className={`px-1.5 py-0.5 text-[0.65rem] font-semibold ${
                              m.impact === "high"
                                ? "bg-ek-purple/10 text-ek-purple"
                                : "bg-ek-canvas text-ek-ink/65"
                            }`}
                          >
                            {m.impact}
                          </span>
                          <span className="bg-ek-canvas px-1.5 py-0.5 text-[0.65rem] text-ek-ink/65">
                            {EFFORT_LABEL[m.effort] || m.effort} · {m.duration_weeks}w
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
